import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2, RefreshCw, X } from "lucide-react";
import { api, formatApiError, imageUrl } from "../lib/api";

const TYPES = ["image/jpeg", "image/png", "image/webp"];

export const ImageUploadField = ({ value, onChange, token }) => {
  const input = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!TYPES.includes(file.type)) return toast.error("Please choose a JPG, PNG or WEBP image");
    if (file.size > 10 * 1024 * 1024) return toast.error("Image must be 10MB or smaller");
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd);
      onChange(data.path);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      input.current.value = "";
    }
  };

  return (
    <div className="flex items-center gap-4">
      <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" data-testid="event-image-upload-input" onChange={(e) => upload(e.target.files?.[0])} />
      <button
        type="button"
        data-testid="event-image-upload-button"
        onClick={() => input.current.click()}
        disabled={uploading}
        className="flex h-28 w-28 shrink-0 items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 text-slate-500 transition-[border-color,background-color] duration-200 hover:border-blue-400 hover:bg-blue-50"
      >
        {uploading ? <Loader2 className="h-6 w-6 animate-spin text-blue-700" /> : value ? <img src={imageUrl(value, token)} alt="" className="h-full w-full object-cover" data-testid="event-image-thumb" /> : <ImagePlus className="h-7 w-7" />}
      </button>
      <div className="space-y-2 text-sm">
        <p className="font-semibold text-slate-800">{value ? "Looking good." : "Add a cover image"}</p>
        <p className="text-slate-500">JPG, PNG or WEBP up to 10MB.</p>
        <div className="flex gap-2">
          <button type="button" data-testid="event-image-change-btn" onClick={() => input.current.click()} className="inline-flex items-center gap-1 font-semibold text-blue-700 hover:text-blue-800">
            <RefreshCw className="h-3.5 w-3.5" /> {value ? "Replace" : "Upload"}
          </button>
          {value && (
            <button type="button" data-testid="event-image-remove-btn" onClick={() => onChange(null)} className="inline-flex items-center gap-1 font-semibold text-slate-500 hover:text-slate-800">
              <X className="h-3.5 w-3.5" /> Remove
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
